import { supabase } from '../lib/supabase';
import type { AmbassadorRankLevel } from '../lib/aos/constants';

export type AmbassadorOsRow = {
  id: string;
  profile_id: string | null;
  full_name: string;
  email: string;
  school: string | null;
  city: string | null;
  state: string | null;
  tier: string;
  status: string;
  rank_level: AmbassadorRankLevel;
  xp: number;
  ambassador_score: number;
  specialty: string | null;
  custom_referral_code: string | null;
  bio: string | null;
  social_links: Record<string, string>;
  total_clicks: number;
  total_sales: number;
  revenue_generated_cents: number;
  commission_earned_cents: number;
  pending_payout_cents: number;
  users_onboarded: number;
  users_converted: number;
  tasks_completed: number;
  content_submitted: number;
  sellers_recruited: number;
  chapter_slug: string | null;
  commission_rate: number;
  stipend_monthly: number;
};

export type AosTask = {
  id: string;
  ambassador_id: string;
  title: string;
  description: string | null;
  category: string;
  priority: string;
  reward_xp: number;
  reward_cents: number;
  due_at: string | null;
  status: string;
  completed_at: string | null;
  created_at: string;
};

export type AosMission = {
  id: string;
  slug: string;
  title: string;
  description: string;
  mission_type: string;
  target_metric: string;
  target_value: number;
  reward_xp: number;
  reward_cents: number;
  reward_label: string | null;
};

export type AosMissionProgress = AosMission & {
  progress_id: string;
  current_value: number;
  progress_status: string;
};

export type AosContentSubmission = {
  id: string;
  ambassador_id: string;
  platform: string;
  content_url: string;
  caption: string | null;
  views: number;
  likes: number;
  status: string;
  created_at: string;
};

export type AosSellerLead = {
  id: string;
  store_name: string;
  instagram: string | null;
  website: string | null;
  contact_info: string | null;
  category: string | null;
  location: string | null;
  status: string;
  created_at: string;
};

export type AosDashboard = {
  is_ambassador: boolean;
  ambassador?: AmbassadorOsRow;
  referral_code?: string;
  leaderboard_position?: number;
  tasks_due?: number;
  admin?: {
    pending_content: number;
    new_applications: number;
    total_ambassadors: number;
    pending_payouts_cents: number;
  };
};

export const ambassadorOsService = {
  async getDashboard(): Promise<{ data: AosDashboard | null; error: Error | null }> {
    const { data, error } = await supabase.rpc('get_my_ambassador_os_dashboard');
    if (error) return { data: null, error: error as Error };
    return { data: data as AosDashboard, error: null };
  },

  async getMyTasks(ambassadorId: string): Promise<{ data: AosTask[]; error: Error | null }> {
    const { data, error } = await supabase
      .from('aos_task_assignments')
      .select('*')
      .eq('ambassador_id', ambassadorId)
      .order('due_at', { ascending: true, nullsFirst: false })
      .limit(50);
    return { data: (data || []) as AosTask[], error: error as Error | null };
  },

  async completeTask(taskId: string) {
    return supabase.rpc('complete_aos_task', { p_task_id: taskId });
  },

  async getMissions(ambassadorId: string): Promise<{ data: AosMissionProgress[]; error: Error | null }> {
    const { data: missions, error: mErr } = await supabase
      .from('aos_missions')
      .select('*')
      .eq('is_active', true)
      .order('created_at');
    if (mErr) return { data: [], error: mErr as Error };

    const { data: progress } = await supabase
      .from('aos_mission_progress')
      .select('*')
      .eq('ambassador_id', ambassadorId);

    const progMap = new Map((progress || []).map((p) => [p.mission_id, p]));

    const merged = (missions || []).map((m) => {
      const p = progMap.get(m.id);
      return {
        ...m,
        progress_id: p?.id ?? '',
        current_value: p?.current_value ?? 0,
        progress_status: p?.status ?? 'active',
      } as AosMissionProgress;
    });
    return { data: merged, error: null };
  },

  async getLeaderboard(limit = 20) {
    const { data, error } = await supabase
      .from('ambassador_crm')
      .select('id, full_name, city, rank_level, ambassador_score, total_sales, revenue_generated_cents, xp')
      .eq('status', 'active')
      .order('ambassador_score', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  },

  async getMyContent(ambassadorId: string) {
    const { data, error } = await supabase
      .from('aos_content_submissions')
      .select('*')
      .eq('ambassador_id', ambassadorId)
      .order('created_at', { ascending: false })
      .limit(30);
    return { data: (data || []) as AosContentSubmission[], error };
  },

  async submitContent(input: {
    ambassador_id: string;
    platform: string;
    content_url: string;
    caption?: string;
  }) {
    const { data, error } = await supabase
      .from('aos_content_submissions')
      .insert({
        ambassador_id: input.ambassador_id,
        platform: input.platform,
        content_url: input.content_url,
        caption: input.caption || null,
      })
      .select('id')
      .single();
    if (!error) {
      const { data: amb } = await supabase
        .from('ambassador_crm')
        .select('content_submitted')
        .eq('id', input.ambassador_id)
        .maybeSingle();
      if (amb) {
        await supabase
          .from('ambassador_crm')
          .update({ content_submitted: (amb.content_submitted || 0) + 1 })
          .eq('id', input.ambassador_id);
      }
    }
    return { data, error };
  },

  async submitSellerLead(input: {
    ambassador_id: string;
    store_name: string;
    instagram?: string;
    website?: string;
    contact_info?: string;
    category?: string;
    location?: string;
    notes?: string;
  }) {
    return supabase.from('aos_seller_leads').insert(input).select('id').single();
  },

  async getMySellerLeads(ambassadorId: string) {
    const { data, error } = await supabase
      .from('aos_seller_leads')
      .select('*')
      .eq('ambassador_id', ambassadorId)
      .order('created_at', { ascending: false });
    return { data: (data || []) as AosSellerLead[], error };
  },

  async getAnnouncements() {
    const { data, error } = await supabase
      .from('aos_announcements')
      .select('*')
      .order('published_at', { ascending: false })
      .limit(10);
    return { data: data || [], error };
  },

  async listAllAnnouncements() {
    const { data, error } = await supabase
      .from('aos_announcements')
      .select('*')
      .order('published_at', { ascending: false })
      .limit(50);
    return { data: data || [], error };
  },

  async createAnnouncement(input: {
    title: string;
    body: string;
    priority?: 'normal' | 'high' | 'urgent';
    expires_at?: string | null;
  }) {
    return supabase.from('aos_announcements').insert({
      title: input.title,
      body: input.body,
      priority: input.priority ?? 'normal',
      expires_at: input.expires_at ?? null,
    });
  },

  async deleteAnnouncement(id: string) {
    return supabase.from('aos_announcements').delete().eq('id', id);
  },

  async getChapters() {
    const { data, error } = await supabase.from('aos_chapters').select('*').order('city');
    return { data: data || [], error };
  },

  async getOpportunities() {
    const { data, error } = await supabase
      .from('aos_opportunities')
      .select('*')
      .eq('is_open', true)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async applyOpportunity(opportunityId: string, pitch?: string) {
    return supabase.rpc('apply_aos_opportunity', {
      p_opportunity_id: opportunityId,
      p_pitch: pitch ?? null,
    });
  },

  async getContentVault() {
    const { data, error } = await supabase
      .from('aos_content_vault')
      .select('*')
      .eq('is_active', true)
      .order('asset_type');
    return { data: data || [], error };
  },

  async getMyPayoutRequests(ambassadorId: string) {
    const { data, error } = await supabase
      .from('aos_payout_requests')
      .select('*')
      .eq('ambassador_id', ambassadorId)
      .order('created_at', { ascending: false })
      .limit(20);
    return { data: data || [], error };
  },

  async requestPayout(amountCents: number, method: string, details: string) {
    return supabase.rpc('request_aos_payout', {
      p_amount_cents: amountCents,
      p_method: method,
      p_payout_details: details,
    });
  },

  async updateProfile(ambassadorId: string, patch: {
    bio?: string;
    specialty?: string;
    social_links?: Record<string, string>;
    custom_referral_code?: string;
  }) {
    return supabase.from('ambassador_crm').update(patch).eq('id', ambassadorId);
  },

  async recordClick(code: string, path?: string) {
    return supabase.rpc('record_aos_referral_click', { p_code: code, p_path: path ?? null });
  },

  /** Staff: pending content queue */
  async listPendingContent() {
    const { data, error } = await supabase
      .from('aos_content_submissions')
      .select('*, ambassador:ambassador_crm(full_name, city)')
      .eq('status', 'pending')
      .order('created_at');
    return { data: data || [], error };
  },

  async reviewContent(id: string, status: 'approved' | 'rejected' | 'changes_requested', admin_notes?: string) {
    return supabase
      .from('aos_content_submissions')
      .update({ status, admin_notes: admin_notes || null, reviewed_at: new Date().toISOString() })
      .eq('id', id);
  },

  async assignTask(ambassadorId: string, templateId: string) {
    const { data: tpl } = await supabase.from('aos_task_templates').select('*').eq('id', templateId).single();
    if (!tpl) return { error: new Error('Template not found') };
    return supabase.from('aos_task_assignments').insert({
      ambassador_id: ambassadorId,
      template_id: templateId,
      title: tpl.title,
      description: tpl.description,
      category: tpl.category,
      priority: tpl.priority,
      reward_xp: tpl.reward_xp,
      reward_cents: tpl.reward_cents,
      due_at: new Date(Date.now() + 7 * 86400000).toISOString(),
    });
  },

  async listAosPayoutRequests(status: 'pending' | 'approved' | 'paid' | 'rejected' = 'pending') {
    const { data, error } = await supabase
      .from('aos_payout_requests')
      .select('*, ambassador:ambassador_crm(full_name, email)')
      .eq('status', status)
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  async approveAosPayout(requestId: string, note?: string) {
    return supabase.rpc('staff_approve_aos_payout', { p_request_id: requestId, p_note: note ?? null });
  },

  async assignDailyTasks(ambassadorId?: string) {
    return supabase.rpc('ensure_daily_aos_tasks', { p_ambassador_id: ambassadorId ?? null });
  },
};
